import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { oktaAuth } from '../../config/auth.config';
import { isLocalEnvironment } from '../../config/environment.config';
import { trackLogin } from '../../services/analyticsService';

interface CallbackState {
  loading: boolean;
  error: string | null;
}

const LoginCallback: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<CallbackState>({
    loading: true,
    error: null,
  });

  useEffect(() => {
    const handleCallback = async () => {
      try {
        setState({ loading: true, error: null });

        // Check if we're in local environment
        if (isLocalEnvironment()) {
          console.log('🏠 Local environment - no callback handling needed, redirecting to home');
          // For local environment, just redirect to home since we don't use Okta callbacks
          navigate('/', { replace: true });
          return;
        }

        console.log('🔐 Non-local environment - handling Okta callback');
        
        // Handle the OAuth callback
        await oktaAuth.handleLoginRedirect();

        // Track successful login
        const user = await oktaAuth.getUser();
        trackLogin(user.sub);

        // Get the intended redirect URL or default to home
        const originalUri = oktaAuth.getOriginalUri();
        const redirectUri = originalUri || '/';

        // Navigate to the intended destination
        navigate(redirectUri, { replace: true });
      } catch (error) {
        console.error('Callback handling error:', error);
        
        let errorMessage = 'Authentication failed. Please try again.';
        if (error instanceof Error) {
          errorMessage = error.message;
        }

        setState({ loading: false, error: errorMessage });
        
        // Redirect to home page after 3 seconds
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      }
    };

    handleCallback();
  }, [navigate]);

  if (state.loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContent}>
          <div style={styles.spinner} />
          <h2 style={styles.title}>Authenticating...</h2>
          <p style={styles.message}>Please wait while we complete your login.</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContent}>
          <h2 style={styles.title}>Authentication Error</h2>
          <p style={styles.errorMessage}>{state.error}</p>
          <p style={styles.message}>You will be redirected to the home page shortly.</p>
        </div>
      </div>
    );
  }

  return null;
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: 'Arial, sans-serif',
  },
  loadingContent: {
    textAlign: 'center' as const,
    padding: '2rem',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    maxWidth: '400px',
    width: '100%',
  },
  errorContent: {
    textAlign: 'center' as const,
    padding: '2rem',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    maxWidth: '400px',
    width: '100%',
    borderLeft: '4px solid #e74c3c',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #007bff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 1rem',
  },
  title: {
    margin: '0 0 1rem 0',
    color: '#333',
    fontSize: '1.5rem',
  },
  message: {
    margin: '0.5rem 0',
    color: '#666',
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  errorMessage: {
    margin: '0.5rem 0 1rem 0',
    color: '#e74c3c',
    fontSize: '1rem',
    fontWeight: 'bold' as const,
  },
};

// Add CSS animation for spinner
const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default LoginCallback;